import React, {useEffect, useMemo, useState} from 'react';
import {Plus} from 'lucide-react';
import SidebarLayout from '../../dashboard/components/SidebarLayout';
import {AppTitle} from '../../../Presentation/components/ui/Typography';
import Card from '../../../Presentation/components/ui/Card';
import Button from '../../../Presentation/components/ui/Button';
import ConfirmationDialog from '../../../Presentation/components/ui/ConfirmationDialog';
import {useAuthorization} from '../../auth/hooks/useAuthorization';
import {useNotification} from '../../../Services/useNotification';
import StandardizedErrorHandler from '../../../Services/common/StandardizedErrorHandler';
import {PAGINATION_CONFIG} from '../../../constants/appConfig';
import {PERMISSIONS} from '../../../constants/permissions';
import {useChecklistsStore} from '../store/checklistsStore';
import ChecklistsTable from '../components/ChecklistsTable';
import ChecklistFormModal from '../components/ChecklistFormModal';
import {
  Checklist,
  ChecklistPayload,
  ChecklistStatus,
  STATUS_LABELS
} from '../models/checklist.model';

const ChecklistsManagementPage: React.FC = () => {
  const {hasPermission} = useAuthorization();
  const {success: showSuccess, error: showError} = useNotification();

  const canCreate = hasPermission(PERMISSIONS.CREATE_CHECKLISTS);
  const canUpdate = hasPermission(PERMISSIONS.UPDATE_CHECKLISTS);
  const canDelete = hasPermission(PERMISSIONS.DELETE_CHECKLISTS);

  const {checklists, pagination, loading, error, fetchChecklists, getChecklist,
    createChecklist, updateChecklist, setChecklistStatus, deleteChecklist} = useChecklistsStore();

  const [statusFilter, setStatusFilter] = useState<ChecklistStatus | ''>('');
  const [search, setSearch] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [editing, setEditing] = useState<Checklist | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Checklist | null>(null);

  const filters = useMemo(
    () => ({
      ...(statusFilter ? {status: statusFilter} : {}),
      ...(search.trim() ? {search: search.trim()} : {})
    }),
    [statusFilter, search]
  );

  useEffect(() => {
    fetchChecklists(1, PAGINATION_CONFIG.DEFAULT_PAGE_SIZE, filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    fetchChecklists(1, PAGINATION_CONFIG.DEFAULT_PAGE_SIZE, filters);
  };

  const handlePageChange = (page: number) =>
    fetchChecklists(page, pagination?.limit || PAGINATION_CONFIG.DEFAULT_PAGE_SIZE, filters);
  const handlePageSizeChange = (size: number) => fetchChecklists(1, size, filters);

  const openCreate = () => {
    setModalMode('create');
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = async (row: Checklist) => {
    try {
      // Fetch full detail (list payload omits items)
      const full = await getChecklist(row.id);
      setEditing(full);
      setModalMode('edit');
      setModalOpen(true);
    } catch {
      showError('تعذّر تحميل تفاصيل القائمة');
    }
  };

  const handleSubmit = async (payload: ChecklistPayload) => {
    setSaving(true);
    try {
      if (modalMode === 'create') {
        await createChecklist(payload);
        showSuccess('تم إنشاء قائمة التحقق بنجاح');
      } else if (editing) {
        await updateChecklist(editing.id, payload);
        showSuccess('تم تحديث قائمة التحقق بنجاح');
      }
      setModalOpen(false);
    } catch (err: unknown) {
      showError(StandardizedErrorHandler.getInstance().toAppError(err).message || 'فشل حفظ قائمة التحقق');
    } finally {
      setSaving(false);
    }
  };

  const handleSetStatus = async (row: Checklist, status: ChecklistStatus) => {
    try {
      await setChecklistStatus(row.id, status);
      showSuccess(`تم تغيير الحالة إلى "${STATUS_LABELS[status]}"`);
    } catch (err: unknown) {
      showError(StandardizedErrorHandler.getInstance().toAppError(err).message || 'فشل تغيير الحالة');
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteChecklist(deleteTarget.id);
      showSuccess('تم حذف قائمة التحقق');
    } catch (err: unknown) {
      showError(StandardizedErrorHandler.getInstance().toAppError(err).message || 'فشل حذف قائمة التحقق');
    } finally {
      setDeleteTarget(null);
    }
  };

  return (
    <SidebarLayout>
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <AppTitle>قوائم التحقق</AppTitle>
            <p className="text-gray-600 mt-1">إنشاء وإدارة قوائم التحقق الديناميكية للزيارات</p>
          </div>
          {canCreate && (
            <Button onClick={openCreate} icon={<Plus className="w-4 h-4" />}>
              إنشاء قائمة تحقق
            </Button>
          )}
        </div>

        {error && (
          <Card className="p-4 bg-red-50 border border-red-200">
            <div className="text-red-700">{error}</div>
          </Card>
        )}

        <Card className="p-6">
          <form className="flex gap-4 flex-wrap items-center" onSubmit={handleSearchSubmit}>
            <input
              className="flex-1 min-w-[220px] border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-primary/30"
              placeholder="بحث بالاسم أو الوصف"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <select
              className="border border-gray-300 rounded-md px-3 py-2"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as ChecklistStatus | '')}
            >
              <option value="">كل الحالات</option>
              {(Object.keys(STATUS_LABELS) as ChecklistStatus[]).map((s) => (
                <option key={s} value={s}>{STATUS_LABELS[s]}</option>
              ))}
            </select>
            <Button type="submit" variant="outline">بحث</Button>
          </form>
        </Card>

        <ChecklistsTable
          checklists={checklists}
          loading={loading}
          page={pagination?.page || 1}
          pageSize={pagination?.limit || PAGINATION_CONFIG.DEFAULT_PAGE_SIZE}
          totalCount={pagination?.total || 0}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          canUpdate={canUpdate}
          canDelete={canDelete}
          onEdit={openEdit}
          onSetStatus={handleSetStatus}
          onDelete={(row) => setDeleteTarget(row)}
        />

        <ChecklistFormModal
          open={modalOpen}
          mode={modalMode}
          initial={editing}
          isLoading={saving}
          onClose={() => setModalOpen(false)}
          onSubmit={handleSubmit}
        />

        <ConfirmationDialog
          open={!!deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={confirmDelete}
          title="حذف قائمة التحقق"
          message={`هل أنت متأكد من حذف "${deleteTarget?.name}"؟`}
          confirmText="حذف"
        />
      </div>
    </SidebarLayout>
  );
};

export default ChecklistsManagementPage;
